/**
 * Warstwa API modułu: I-b — onboarding (okno kadr + projekcja IT).
 * Właściciel: FALA 1, agent I-b-1 (I-b-2..5 dopisują swoje) (docs/PROJEKT-00-FUNDAMENT-2026-09-02.md §5, §6).
 * Trasy backendu: api/hr-onboarding, api/itsm-onboarding.
 *
 * Kontrakt: `attach(api)` dostaje instancję axios z apiService.js (interceptory JWT, cache GET, obsługa 401)
 * i zwraca OBIEKT nazwanych metod, które apiService.js doczepia do wspólnego `apiService` (jedno miejsce,
 * src/api/modules/index.js). Nazwy metod muszą być unikalne w całej aplikacji — kolizja zatrzymuje start
 * (patrz attachApiModules). Tego pliku nie dotyka nikt poza właścicielem modułu.
 *
 * UWAGA: pozycji checklisty NIE MA tutaj — czyta je i odhacza wspólne `hrProcess.js`
 * (`getHrProcessCaseTasks("onboarding", caseId)`). Druga ścieżka do tych samych pozycji oznaczałaby
 * drugą autoryzację odznaczania, a fundament §1.3 dopuszcza dokładnie jedną.
 */
export function attach(api) {
    return {
        // ── Okno kadr (klucz HrOnboardView) ─────────────────────────────
        /**
         * @param {"aktywne"|"zakonczone"|"wszystkie"} [state]
         * @param {string} [q] szukanie po nazwisku, stanowisku, numerze zgłoszenia
         */
        getOnboardingCases: (state, q) =>
            api.get("/hr-onboarding/cases", { params: { state, q } }),

        /**
         * [OBSZAR 11, 2026-09-18] Wnioski o zatrudnienie, które JESZCZE nie stały się sprawą — ze stanem akceptacji.
         * Lista samych spraw gubiła świeżo złożony wniosek z oczu („W toku 0” zaraz po zielonym potwierdzeniu).
         * @param {"aktywne"|"zakonczone"|"wszystkie"} [state]
         * @param {string} [q] szukanie po nazwisku, stanowisku, numerze wniosku
         */
        getOnboardingPendingRequests: (state, q) =>
            api.get("/hr-onboarding/pending-requests", { params: { state, q } }),

        getOnboardingCase: (id) => api.get(`/hr-onboarding/cases/${id}`),

        getOnboardingCaseTimeline: (id) => api.get(`/hr-onboarding/cases/${id}/timeline`),

        /**
         * Karta „Osoba" — projekcja teczki wstępnej BEZ PESEL-u i adresu (tylko fakt ich wypełnienia).
         * Odpowiedź: `{ osoba, powod? }`; `osoba === null` znaczy „sprawa nie ma jeszcze teczki wstępnej".
         */
        getOnboardingCasePerson: (id) => api.get(`/hr-onboarding/cases/${id}/person`),

        /** Teczki wstępne robocze do wskazania w szufladzie „Wskaż teczkę wstępną". */
        getOnboardingOsobyWstepne: (id, q) =>
            api.get(`/hr-onboarding/cases/${id}/osoby-wstepne`, { params: { q } }),

        /** To samo, co widzi IT — żeby kadry mogły sprawdzić, co wyszło do zgłoszenia. */
        getOnboardingCaseItView: (id) => api.get(`/hr-onboarding/cases/${id}/it-view`),

        // ── Zapis (klucz HrOnboardManage) ───────────────────────────────
        /** @param {object} data sekcja + pola; `sekcja` ogranicza zapis do tej części szuflady. */
        updateOnboardingCaseData: (id, data) => api.put(`/hr-onboarding/cases/${id}/data`, data),

        linkOnboardingOsobaWstepna: (id, osobaWstepnaId) =>
            api.post(`/hr-onboarding/cases/${id}/link-osoba-wstepna`, { osobaWstepnaId }),

        requestOnboardingAccount: (id, data = {}) =>
            api.post(`/hr-onboarding/cases/${id}/request-account`, data),

        createOnboardingItTicket: (id) => api.post(`/hr-onboarding/cases/${id}/it-ticket`, {}),

        // ── Zakładka IT `/admin/onboarding` (klucz ItOnboardHandle) ──────
        // Projekcja TECHNICZNA: bez PESEL-u, adresu, formy zatrudnienia i notatek kadrowych.
        // Odhaczanie pozycji NIE JEST tutaj — idzie przez wspólne `hrProcess.js` (jedna autoryzacja).
        /** @param {"open"|"closed"} [state] */
        getItOnboardingCases: (state, q) => api.get("/itsm-onboarding/cases", { params: { state, q } }),

        getItOnboardingByTicket: (ticketId) => api.get(`/itsm-onboarding/by-ticket/${ticketId}`),

        getItOnboardingTimeline: (caseId) => api.get(`/itsm-onboarding/cases/${caseId}/timeline`),

        // ── Automatyzacje: szkolenia i dokumenty (etap 4) ───────────────
        // Stan automatów sprawy: kursy przypisane i planowane, pakiet dokumentów, wydania, braki danych
        // i JEDNO zdanie „dlaczego jeszcze nie da się wydać kompletu".
        getOnboardingAutomation: (id) => api.get(`/hr-onboarding/cases/${id}/automation`),

        /** @param {{courseId:number, dueDate?:string, isMandatory?:boolean}} data */
        assignOnboardingCourse: (id, data) => api.post(`/hr-onboarding/cases/${id}/courses`, data),

        /** Ponowne przypisanie kursów z konfiguracji — po zmianie formy zatrudnienia. */
        syncOnboardingCourses: (id) => api.post(`/hr-onboarding/cases/${id}/courses/sync`, {}),

        /**
         * Wydanie kompletu dokumentów zatrudnienia. Bajty zostają w rejestrze wydań —
         * plik pobiera się przez `/document-issues/{id}/pdf` (jedno źródło, fundament §1.1).
         * @param {{bundleCode?:string, excludedTemplateCodes?:string[], createContractRow?:boolean}} data
         */
        generateOnboardingDocuments: (id, data = {}) =>
            api.post(`/hr-onboarding/cases/${id}/documents/generate`, data),

        /**
         * ZAPISANY plik wydania z rejestru (fundament §1.1) — nigdy render na żądanie.
         * `responseType: "blob"` jest konieczne: bez niego axios potraktowałby PDF jak tekst
         * i plik doszedłby uszkodzony, a dokument o mocy prawnej ma dojść bajt w bajt taki,
         * jaki został wydany (reguła właściciela #2).
         */
        getOnboardingIssuePdf: (issueId) =>
            api.get(`/document-issues/${issueId}/pdf`, { responseType: "blob" }),

        // ── Konfiguracja automatów (klucz HrOnboardManage do zapisu) ─────
        getOnboardingConfig: () => api.get("/hr-onboarding/config"),
        saveOnboardingConfig: (data) => api.put("/hr-onboarding/config", data),

        /**
         * Czy STARY moduł wniosków sprzętowych jest już zamknięty na zapis.
         * Archiwalna strona pyta o to, zanim napisze baner — baner „zapis wyłączony" postawiony
         * na stałe kłamałby przez cały okres przejściowy, w którym stary moduł jeszcze działa.
         * Odpowiedź: `{ frozen, replacement }`.
         */
        getOnboardingArchiveState: () => api.get("/onboarding/archive-state"),

        // ── Domknięcie sprawy (etap 5) ──────────────────────────────────
        getOnboardingSummary: () => api.get("/hr-onboarding/summary"),

        /** Co blokuje zakończenie — czytane PRZED kliknięciem, żeby przycisk mówił, czego brakuje. */
        getOnboardingCompletionBlockers: (id) =>
            api.get(`/hr-onboarding/cases/${id}/completion-blockers`),

        completeOnboardingCase: (id) => api.post(`/hr-onboarding/cases/${id}/complete`, {}),

        /** @param {string} reason powód jest OBOWIĄZKOWY — sprawa zostaje w rejestrze na zawsze. */
        cancelOnboardingCase: (id, reason) =>
            api.post(`/hr-onboarding/cases/${id}/cancel`, { reason }),

        // ── Schemat działu: wakaty „w rekrutacji" (klucz ModuleMojDzial) ─
        // Odpowiedź: { vacancies: [...] }. Endpoint jest w module „Mój Dział", bo to jego zasięg
        // rozstrzyga, czyje wakaty widać — nie klucz kadrowy.
        getMyDeptOrgStructureCards: () => api.get("/my-dept/org-structure"),
    };
}
